"use client";

import { useEffect, useState } from "react";

/** How long a given error digest stays "already auto-retried" before another auto-retry is allowed. */
const AUTO_RETRY_WINDOW_MS = 10_000;

/**
 * Auto-recovers a Next.js error boundary once for transient errors — e.g. a
 * cold-start SSR failure ("Failed to load external …") that succeeds on a warm
 * retry — instead of surfacing the error card to the user.
 *
 * Only **server** errors are retried: Next.js attaches a stable `digest` to
 * server errors (and strips the message in production), whereas client errors
 * have no digest and frequently share generic/varying messages. Keying on a
 * varying value would let `reset()` loop forever (a self-inflicted DoS), so
 * errors without a `digest` fall straight through to the card.
 *
 * Loop prevention is **storage-driven, not dependency-array-driven**: `error`
 * and `reset` are fresh references on every boundary render, so the effect
 * re-fires after each `reset()`. The `sessionStorage` guard — keyed on the
 * stable `digest` — is what caps auto-retry to once per digest within
 * {@link AUTO_RETRY_WINDOW_MS}. A genuinely broken page therefore still shows
 * the card after a single silent retry.
 *
 * @param error - The error passed to the error boundary (`digest` set for server errors).
 * @param reset - The boundary's `reset()` callback that re-renders the failed segment.
 * @returns `true` while an auto-retry is in progress — render a neutral placeholder, not the card.
 */
export function useErrorAutoRetry(error: Error & { digest?: string }, reset: () => void): boolean {
  // States
  const [isRetrying, setIsRetrying] = useState(false);

  // Effects
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Without a stable digest we can't safely de-dupe retries — show the card.
    if (!error.digest) {
      setIsRetrying(false);
      return;
    }

    const key = `error-boundary-retry:${error.digest}`;

    let retriedAt: number | null = null;
    try {
      const stored = window.sessionStorage.getItem(key);
      retriedAt = stored ? Number(stored) : null;
    } catch {
      return; // sessionStorage unavailable (private mode / quota) — fall through to the card
    }

    const now = Date.now();
    const alreadyRetried = retriedAt !== null && Number.isFinite(retriedAt) && now - retriedAt < AUTO_RETRY_WINDOW_MS;
    if (alreadyRetried) {
      // The retry already happened (or the instance was reused) — recover to the card.
      setIsRetrying(false);
      return;
    }

    try {
      window.sessionStorage.setItem(key, String(now));
    } catch {
      return; // can't persist the guard — skip auto-retry to avoid a reset() loop
    }

    setIsRetrying(true);
    reset();
  }, [error, reset]);

  return isRetrying;
}
